import type {Evaluator} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {z} from "zod/v4-mini"
import {MeshNode} from "#template/nodes/object/mesh/mesh-node"
import type {MeshNodeDefinition, MeshNodeParameters} from "#template/nodes/object/mesh/types"
import {meshNodeParameters} from "#template/nodes/object/mesh/validation"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import type {CurveLikeNode} from "#template/nodes/types"
import {curveLike, isCurveLikeNode} from "#template/nodes/object/curve/fundamental"
import type {OnCompileContext, OnFilterActiveContext, TemplateNodeContext, VisitorNodeResult} from "#template/template-node/types"
import {skipped} from "#template/template-node/constants"
import {MeshCurve} from "#template/nodes/object/curve/mesh-curve"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {ObjectData, CurveObjectData} from "#template/interfaces/object-data"
import {isCurveObjectData} from "#template/interfaces/object-data"
import {GenerateMesh} from "#template/runtime-graph/nodes/generate-mesh"
import {hashObject} from "@cm/utils/hashing"
import type {MeshNodes} from "@cm/render-nodes"
import type {AttributeRef} from "#template/geometry-processing/geometry-graph"
import {GeomBuilderContext, geomToMesh, meshToGeom, Operators, continuousCurveAttr, Primitives} from "#template/geometry-processing/geometry-graph"
import {NodesInput} from "#template/fundamental/input/nodes-input"
import {isEvaluatedTemplateNode} from "#template/utils/template-node-utils"
import {visitNone} from "#template/template-node/visit-helpers"

function prepareBaseCurveData(
    ctx: GeomBuilderContext,
    meshCurve?: {
        baseMeshGraph: MeshNodes.Mesh
        curvePoints: Float32Array
        curveSpace: "position" | "uv"
        numMeshUVChannels: number
        meshUVChannel?: number
    },
    nonMeshCurve?: {
        curvePoints: Float32Array
        curveNormals: Float32Array
    },
) {
    if (meshCurve && nonMeshCurve) throw new Error("Expected either mesh curve or non-mesh curve, both provided")

    if (meshCurve) {
        const {baseMeshGraph, curvePoints, curveSpace, numMeshUVChannels, meshUVChannel} = meshCurve

        const baseMesh = meshToGeom(ctx, baseMeshGraph, numMeshUVChannels)

        const projectOrJoinMeshCurve = (curvePointsBuffer: Float32Array, curveSpace: "position" | "uv") => {
            if (curveSpace === "uv") {
                if (!meshUVChannel) {
                    throw new Error("meshUVChannel is required for curveSpace === 'uv'")
                }
                const inputCurve = Primitives.curve(ctx, {
                    uv: continuousCurveAttr(curvePointsBuffer, "float2"),
                })
                return Operators.join(inputCurve.uv, inputCurve, baseMesh.uvs[meshUVChannel], baseMesh)[1]
            } else {
                const inputCurve = Primitives.curve(ctx, {
                    position: continuousCurveAttr(curvePointsBuffer, "float3"),
                })
                return Operators.project(inputCurve.position, inputCurve, baseMesh.position, baseMesh)
            }
        }

        return {
            attributes: projectOrJoinMeshCurve(curvePoints, curveSpace),
            curvePoints,
        }
    }

    if (nonMeshCurve) {
        return {
            attributes: Primitives.curve(ctx, {
                position: continuousCurveAttr(nonMeshCurve.curvePoints, "float3"),
                normal: continuousCurveAttr(nonMeshCurve.curveNormals, "float3"),
            }),
            curvePoints: nonMeshCurve.curvePoints,
        }
    }

    throw new Error("Expected either mesh curve or non-mesh curve, none provided")
}

function applyProfileExtrusion(
    ctx: GeomBuilderContext,
    profileCurve: {
        positions: Float32Array
        sectionIds: Uint32Array
    },
    baseCurve: {
        attributes: {
            position: AttributeRef
            normal: AttributeRef
        }
        curvePoints: Float32Array
    },
    generateFrontCap: boolean,
    generateBackCap: boolean,
): MeshNodes.Mesh {
    const baseCurveVertexCount = baseCurve.curvePoints.length / 3
    const profileCurveVertexCount = profileCurve.positions.length / 3

    const sectionIdCount = (() => {
        const sectionIdSet = new Set<number>()
        for (let i = 0; i < profileCurve.sectionIds.length; i++) {
            sectionIdSet.add(profileCurve.sectionIds[i])
        }
        return sectionIdSet.size
    })()

    const curveFilterAttribute = (vertexCount: number, filter: "first" | "last") => {
        const buffer: number[] = []
        for (let i = 0; i < vertexCount; i++) buffer.push(0)
        if (filter === "first") buffer[0] = 1
        if (filter === "last") buffer[vertexCount - 1] = 1
        return Primitives.curve(ctx, {filter: continuousCurveAttr(new Float32Array(buffer), "float")}).filter
    }

    const curveTAttribute = (vertexCount: number) => {
        const buffer: number[] = []
        for (let i = 0; i < vertexCount; i++) buffer.push(i / (vertexCount - 1))
        return Primitives.curve(ctx, {t: continuousCurveAttr(new Float32Array(buffer), "float")}).t
    }

    const baseCurveFullAttributeSet = (() => {
        const baseU = baseCurve.attributes.position.flatten()
        const baseTangent = Operators.diff(baseCurve.attributes.position, baseU).normalize()
        const baseBitangent = baseCurve.attributes.normal.cross(baseTangent).normalize()

        return {
            position: baseCurve.attributes.position,
            normal: baseCurve.attributes.normal,
            u: baseU,
            tangent: baseTangent,
            bitangent: baseBitangent,
            filterFirst: curveFilterAttribute(baseCurveVertexCount, "first"),
            filterLast: curveFilterAttribute(baseCurveVertexCount, "last"),
            t: curveTAttribute(baseCurveVertexCount),
        }
    })()

    const profileCurveFullAttributeSet = (() => {
        const attributeSet = Primitives.curve(ctx, {
            position: continuousCurveAttr(profileCurve.positions, "float3"),
            sectionId: continuousCurveAttr(profileCurve.sectionIds, "uint32"),
        })

        return {
            position: attributeSet.position,
            v: attributeSet.position.flatten(),
            sectionId: attributeSet.sectionId,
            t: curveTAttribute(profileCurveVertexCount),
        }
    })()

    const outputAttributeSets: {position: AttributeRef; normal: AttributeRef; uvs: AttributeRef[]; materialID: AttributeRef}[] = []

    {
        const [prodBaseCurve, prodProfileCurve] = Operators.product(
            baseCurveFullAttributeSet.position,
            baseCurveFullAttributeSet,
            profileCurveFullAttributeSet.position,
            profileCurveFullAttributeSet,
        )

        const position = prodBaseCurve.position
            .add(prodBaseCurve.bitangent.mul(prodProfileCurve.position.x))
            .add(prodBaseCurve.normal.mul(prodProfileCurve.position.y))

        const texUV = Operators.pack(prodBaseCurve.u, prodProfileCurve.v)
        const normalizedUV = Operators.pack(prodBaseCurve.t, prodProfileCurve.t)

        outputAttributeSets.push({
            position,
            normal: position.normals(1),
            uvs: [texUV, normalizedUV],
            materialID: prodProfileCurve.sectionId,
        })
    }

    if (generateFrontCap || generateBackCap) {
        const {xyAttribute: profileXYAttribute, xyBounds: profileXYBounds} = (() => {
            const {xyAttributeData, xyBounds} = (() => {
                const primitive: [number, number][] = []

                const xyBounds = {
                    min: [Infinity, Infinity],
                    max: [-Infinity, -Infinity],
                }

                for (let i = 0; i < profileCurveVertexCount - 1; i++) {
                    const curveVertexXY = [profileCurve.positions[i * 3], profileCurve.positions[i * 3 + 1]] as [number, number]
                    primitive.push(curveVertexXY)

                    if (curveVertexXY[0] < xyBounds.min[0]) xyBounds.min[0] = curveVertexXY[0]
                    if (curveVertexXY[1] < xyBounds.min[1]) xyBounds.min[1] = curveVertexXY[1]
                    if (curveVertexXY[0] > xyBounds.max[0]) xyBounds.max[0] = curveVertexXY[0]
                    if (curveVertexXY[1] > xyBounds.max[1]) xyBounds.max[1] = curveVertexXY[1]
                }

                return {xyAttributeData: ["float2" as const, [primitive] as (readonly [number, number])[][]] as const, xyBounds}
            })()

            return {
                xyAttribute: Primitives.mesh(ctx, {xy: xyAttributeData}).xy,
                xyBounds,
            }
        })()

        const profileWidth = profileXYBounds.max[0] - profileXYBounds.min[0]
        const profileHeight = profileXYBounds.max[1] - profileXYBounds.min[1]

        const texUV = Operators.pack(profileXYAttribute.x.sub(profileXYBounds.min[0]), profileXYAttribute.y.sub(profileXYBounds.min[1]))
        const normalizedUV = Operators.pack(texUV.x.div(profileWidth), texUV.y.div(profileHeight))

        const baseCurveBoundary = Operators.boundary(baseCurveFullAttributeSet.position, baseCurveFullAttributeSet)

        const capBaseAttributeSet = {
            position: Operators.pack(profileXYAttribute.x, profileXYAttribute.y, profileXYAttribute.constFloat(0)),
            texUV,
            normalizedUV,
        }

        const [prodBaseCurveBoundary, prodCapBase] = Operators.product(
            baseCurveBoundary.position,
            baseCurveBoundary,
            capBaseAttributeSet.position,
            capBaseAttributeSet,
        )

        const position = prodBaseCurveBoundary.position
            .add(prodBaseCurveBoundary.bitangent.mul(prodCapBase.position.x))
            .add(prodBaseCurveBoundary.normal.mul(prodCapBase.position.y))

        const normal = position.normals(1)

        let materialID = sectionIdCount
        if (generateFrontCap && prodBaseCurveBoundary.filterFirst) {
            const capAttributeSet = {
                position,
                normal,
                filterFirst: prodBaseCurveBoundary.filterFirst,
                texUV: Operators.pack(prodCapBase.texUV.x.sub(profileWidth + 1), prodCapBase.texUV.y),
                normalizedUV: Operators.pack(prodCapBase.normalizedUV.x.sub(2), prodCapBase.normalizedUV.y),
            }

            const capFullAttributeSet = Operators.filter(capAttributeSet.filterFirst, capAttributeSet)
            outputAttributeSets.push({
                position: capFullAttributeSet.position,
                normal: capFullAttributeSet.normal,
                uvs: [capFullAttributeSet.texUV, capFullAttributeSet.normalizedUV],
                materialID: capFullAttributeSet.position.constInt(materialID++),
            })
        }

        if (generateBackCap && prodBaseCurveBoundary.filterLast) {
            const capAttributeSet = {
                position,
                normal: normal.mul(-1),
                filterLast: prodBaseCurveBoundary.filterLast,
                texUV: Operators.pack(prodCapBase.texUV.x.mul(-1).add(profileWidth), prodCapBase.texUV.y.sub(profileHeight + 1)),
                normalizedUV: Operators.pack(prodCapBase.normalizedUV.x.mul(-1).add(1), prodCapBase.normalizedUV.y.sub(2)),
            }

            const capFullAttributeSet = Operators.flip(Operators.filter(capAttributeSet.filterLast, capAttributeSet))
            outputAttributeSets.push({
                position: capFullAttributeSet.position,
                normal: capFullAttributeSet.normal,
                uvs: [capFullAttributeSet.texUV, capFullAttributeSet.normalizedUV],
                materialID: capFullAttributeSet.position.constInt(materialID++),
            })
        }
    }

    return geomToMesh(Operators.merge(...outputAttributeSets))
}

function setupCurveData<CurveNode extends CurveLikeNode>(
    curveNode: CurveNode | NodesInput,
    evaluator: NodeEvaluator,
    scope: GraphBuilderScope,
    scopeLocalId: string,
) {
    const localScope = scope.scope(scopeLocalId)
    if (isCurveLikeNode(curveNode)) {
        return evaluator.evaluate(curveNode)
    } else {
        return localScope.pureLambda(
            NodesInput.evaluateList(localScope, evaluator, [curveNode]),
            (evaluatedNodes) => {
                const evaluatedNode = evaluatedNodes[0]
                if (evaluatedNode.type === "Mesh" || evaluatedNode.type === "Curve") {
                    const objectData = evaluatedNode.value as ObjectData | null
                    if (objectData && isCurveObjectData(objectData)) return objectData as CurveObjectData
                }
                return null
            },
            "setupCurveData",
        )
    }
}

export type ProfileExtrudeMeshParameters = {
    name: string
    baseCurve: CurveLikeNode | null
    profileCurve: CurveLikeNode | null
    frontCap?: boolean
    backCap?: boolean
} & MeshNodeParameters

const profileExtrudeMeshDefinition: MeshNodeDefinition<ProfileExtrudeMeshParameters> = {
    nodeClass: "ProfileExtrudeMesh",
    validation: {
        parameters: z.extend(
            z.object({
                name: z.string(),
                baseCurve: z.nullable(curveLike),
                profileCurve: z.nullable(curveLike),
                frontCap: z.optional(z.boolean()),
                backCap: z.optional(z.boolean()),
            }),
            meshNodeParameters.shape,
        ),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class ProfileExtrudeMesh extends MeshNode<ProfileExtrudeMeshParameters> {
    public override get definition() {
        return profileExtrudeMeshDefinition
    }

    public override onFilterActive(
        _context: OnFilterActiveContext,
        _evaluator: Evaluator<TemplateNodeContext>,
    ): VisitorNodeResult<ProfileExtrudeMeshParameters> {
        if (this.parameters.baseCurve === null || this.parameters.profileCurve === null) return skipped
        return visitNone(this.parameters)
    }

    public override onCompile(context: OnCompileContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<ProfileExtrudeMeshParameters> {
        const {evaluator} = context
        const {templateContext, activeNodeSet} = evaluator
        const {sceneManager, computeDummyReifiedMeshData, logger} = templateContext
        const {baseCurve, profileCurve, frontCap: generateFrontCap, backCap: generateBackCap} = this.parameters

        if (baseCurve === null || profileCurve === null) return skipped

        const validBaseCurveParameter = (() => {
            if (baseCurve instanceof MeshCurve) return baseCurve.parameters.mesh !== null ? baseCurve : null
            return baseCurve
        })()

        if (!validBaseCurveParameter) return skipped

        const isBaseCurveMeshCurve = validBaseCurveParameter instanceof MeshCurve

        const scope = evaluator.getScope(this)
        const [meshCurveMeshData, meshCurveMeshDataInvalid] = isBaseCurveMeshCurve
            ? scope.branch(evaluator.evaluate(validBaseCurveParameter.parameters.mesh))
            : [null, undefined]

        const [baseCurveDataValid, baseCurveDataInvalid] = scope.branch(setupCurveData(validBaseCurveParameter, evaluator, scope, "baseCurveData"))

        const [profileCurveDataValid, profileCurveDataInvalid] = scope.branch(
            scope.pureLambda(
                setupCurveData(profileCurve, evaluator, scope, "profileCurveData"),
                (curveObjectData) => {
                    if (curveObjectData && isCurveObjectData(curveObjectData as ObjectData)) return curveObjectData as CurveObjectData
                    return null
                },
                "setupProfileCurve",
            ),
        )

        const deferredBaseCurveData = scope.waitForFinishedModifyingScene(baseCurveDataValid, sceneManager)
        const deferredProfileCurveData = scope.waitForFinishedModifyingScene(profileCurveDataValid, sceneManager)

        const [transformedValid, transformedInvalid] = scope.branch(
            scope.pureLambda(
                // pure-lambda-allow: logger
                scope.tuple(deferredProfileCurveData, deferredBaseCurveData, meshCurveMeshData, isBaseCurveMeshCurve, generateFrontCap, generateBackCap),
                ([profileCurveData, baseCurveData, meshCurveMeshData, isBaseCurveMeshCurve, generateFrontCap, generateBackCap]) => {
                    const profileCurvePosition = profileCurveData.completeCurveData.reified?.points
                    if (!profileCurvePosition) {
                        logger.warn("Invalid profile curve data, missing positions buffer")
                        return null
                    }

                    const profileCurveSectionIds = profileCurveData.completeCurveData.reified?.sectionIds
                    if (!profileCurveSectionIds) {
                        logger.warn("Invalid profile curve data, missing sectionIds buffer")
                        return null
                    }

                    const profileCurve = {
                        positions: profileCurvePosition,
                        sectionIds: profileCurveSectionIds,
                    }

                    const meshCurveAsBaseCurve = (() => {
                        if (isBaseCurveMeshCurve && !meshCurveMeshData) return null
                        const curvePoints = baseCurveData?.completeCurveData.reified?.points
                        if (!curvePoints) return null
                        const curveSpace = "position" as const
                        const numMeshUVChannels = meshCurveMeshData?.completeMeshData.reified.uvs.length ?? 0

                        const displayGeometryGraph = meshCurveMeshData?.completeMeshData.abstract.displayGeometryGraph
                        const renderGeometryGraph = meshCurveMeshData?.completeMeshData.abstract.renderGeometryGraph

                        if (!displayGeometryGraph || !renderGeometryGraph) return null

                        return {
                            display: {
                                curvePoints,
                                curveSpace,
                                numMeshUVChannels,
                                baseMeshGraph: displayGeometryGraph,
                            },
                            render: {
                                curvePoints,
                                curveSpace,
                                numMeshUVChannels,
                                baseMeshGraph: renderGeometryGraph,
                            },
                        }
                    })()

                    const nonMeshCurveAsBaseCurve = (() => {
                        if (isBaseCurveMeshCurve) return null
                        const curvePoints = baseCurveData?.completeCurveData.reified?.points
                        if (!curvePoints) return null
                        const curveNormals = baseCurveData?.completeCurveData.reified?.normals
                        if (!curveNormals) return null

                        return {
                            curvePoints,
                            curveNormals,
                        }
                    })()

                    if ((meshCurveAsBaseCurve && nonMeshCurveAsBaseCurve) || (!meshCurveAsBaseCurve && !nonMeshCurveAsBaseCurve)) return null

                    const ctx = new GeomBuilderContext()

                    const baseCurveDisplay = prepareBaseCurveData(
                        ctx,
                        meshCurveAsBaseCurve ? meshCurveAsBaseCurve.display : undefined,
                        nonMeshCurveAsBaseCurve ?? undefined,
                    )

                    const baseCurveRender = prepareBaseCurveData(
                        ctx,
                        meshCurveAsBaseCurve ? meshCurveAsBaseCurve.render : undefined,
                        nonMeshCurveAsBaseCurve ?? undefined,
                    )

                    return {
                        contentHash: hashObject([
                            "profileExtrudeMesh",
                            meshCurveMeshData?.completeMeshData.abstract.contentHash,
                            meshCurveAsBaseCurve?.display.curvePoints,
                            nonMeshCurveAsBaseCurve?.curvePoints,
                            nonMeshCurveAsBaseCurve?.curveNormals,
                            profileCurve.positions,
                            profileCurve.sectionIds,
                            generateFrontCap,
                            generateBackCap,
                        ]),
                        displayGeometryGraph: applyProfileExtrusion(ctx, profileCurve, baseCurveDisplay, generateFrontCap ?? false, generateBackCap ?? false),
                        renderGeometryGraph: applyProfileExtrusion(ctx, profileCurve, baseCurveRender, generateFrontCap ?? false, generateBackCap ?? false),
                        displayGeometryGraphResources: meshCurveMeshData?.completeMeshData.abstract.displayGeometryGraphResources ?? {},
                    }
                },
                "transformedGraphs",
            ),
        )

        const {completeMeshData} = scope.node(GenerateMesh, {
            sceneManager,
            computeDummyReifiedMeshData: computeDummyReifiedMeshData && !isEvaluatedTemplateNode(this, activeNodeSet),
            inputMeshData: transformedValid,
        })

        const invalidator = meshCurveMeshDataInvalid
            ? scope.phi(transformedInvalid, profileCurveDataInvalid, baseCurveDataInvalid, meshCurveMeshDataInvalid)
            : scope.phi(transformedInvalid, profileCurveDataInvalid, baseCurveDataInvalid)

        this.setupMesh(scope, context, completeMeshData, true, invalidator)

        return visitNone(this.parameters)
    }
}
